import useSWR, { mutate, type KeyedMutator } from "swr";

import {
  getImageCategories,
  getPropertyPriceHistory,
  getSeller,
  getSellers,
  getLoggedInSeller,
  getContractDocument,
  getReviewGroupSections,
} from "../api.properties";
import { ISellerDto, PersonaVerificationDto } from "common/middleware/api.services";

const PROPERTIES_IMAGE_CATEGORIES_KEY = "/api/properties/images/categories";
const PROPERTIES_PRICE_HISTORY_KEY = "/api/properties/priceHistory";
const SELLERS_KEY = "/api/sellers";
const LOGGED_IN_SELLER_KEY = "/api/loggedInSeller";
const CONTRACT_DOC_KEY = "/api/contractDocument";
const REVIEW_GROUP_SECTIONS_KEY = "/reviewGroupSections";

export const mutateImageCategories = async (propertyId: number, getDraft: boolean = false) => {
  await mutate([
    PROPERTIES_IMAGE_CATEGORIES_KEY,
    propertyId,
    getDraft
  ]);
};

export const mutateLoggedInSeller = async (
  propertyId: number,
  getIdVerificationStatus: boolean = false,
  getDraft: boolean = false,
) => {
  await mutate([
    LOGGED_IN_SELLER_KEY,
    propertyId,
    getIdVerificationStatus,
    getDraft,
  ]);
}

export const useImageCategories = (
  propertyId: number,
  {
    shouldFetch = true,
    draft = false
  } : {
    shouldFetch?: boolean,
    draft?: boolean
  } = {}
) => {
  const { data, error } = useSWR(
    shouldFetch && propertyId
      ? [
          PROPERTIES_IMAGE_CATEGORIES_KEY,
          propertyId,
          draft
        ]
      : null,
    getImageCategories
  );

  return {
    imageCategories: data,
    isLoading: shouldFetch && !error && !data,
    isError: error,
  };
}

export const usePropertyPriceHistory = (propertyId: number) => {
  const { data, error } = useSWR(
    [PROPERTIES_PRICE_HISTORY_KEY, propertyId],
    getPropertyPriceHistory
  );

  return {
    priceHistory: data,
    isLoading: !error && !data,
    isError: error,
  };
}

export const useSeller = (
  propertyId: number,
  sellerId: string,
  opt?: {
    getDraft?: boolean,
    shouldFetch?: boolean,
  },
) => {
  const shouldFetch = opt?.shouldFetch !== false;

  const { data, error } = useSWR(
    shouldFetch && propertyId && sellerId
      ? [SELLERS_KEY, propertyId, sellerId, opt?.getDraft]
      : null,
    getSeller
  );

  return {
    seller: data,
    isLoading: shouldFetch && Boolean(propertyId) && Boolean(sellerId) && !error && !data,
    isError: error,
  };
}

export type SellerWithIdVerification = ISellerDto & { idVerification: PersonaVerificationDto };

export function useSellers(propertyId: number | undefined, opt?: {
  refreshInterval?: number,
  getIdVerificationStatus?: false,
  shouldFetch?: boolean,
  getDraft?: boolean,
}): {
  sellers: ISellerDto[],
  isLoading: boolean,
  error: any,
  mutateSellers: KeyedMutator<ISellerDto[]>,
};
export function useSellers(propertyId: number | undefined, opt?: {
  refreshInterval?: number,
  getIdVerificationStatus: true,
  shouldFetch?: boolean,
  getDraft?: boolean,
}): {
  sellers: SellerWithIdVerification[],
  isLoading: boolean,
  error: any,
  mutateSellers: KeyedMutator<ISellerDto[]>,
}
export function useSellers(propertyId: number | undefined, opt?: {
  refreshInterval?: number,
  getIdVerificationStatus?: boolean,
  shouldFetch?: boolean,
  getDraft?: boolean,
}) {
  const shouldFetch = opt?.shouldFetch !== false;
  const getIdVerificationStatus = opt?.getIdVerificationStatus ?? false;
  const getDraft = opt?.getDraft ?? false;

  const { data, error, mutate } = useSWR(
    shouldFetch && propertyId
      ? [
          SELLERS_KEY,
          propertyId,
          getIdVerificationStatus,
          getDraft,
        ]
      : null,
    getSellers,
    { refreshInterval: opt?.refreshInterval }
  );

  return {
    sellers: data,
    isLoading: shouldFetch && Boolean(propertyId) && !error && !data,
    error,
    mutateSellers: mutate,
  };
}

export const mutateSellers = async (
  propertyId: number,
  getIdVerificationStatus: boolean = false,
  getDraft: boolean = false,
) => {
  await mutate([SELLERS_KEY, propertyId, getIdVerificationStatus, getDraft]);
}

export function useLoggedInSeller(
  propertyId: number,
  opt?: {
    refreshInterval?: number,
    shouldFetch?: boolean,
    getIdVerificationStatus?: false,
    getDraft?: boolean,
  },
): {
  seller: ISellerDto,
  isLoading: boolean,
  error: any,
  mutateLoggedInSeller: KeyedMutator<ISellerDto>
};
export function useLoggedInSeller(
  propertyId: number,
  opt?: {
    refreshInterval?: number,
    shouldFetch?: boolean,
    getIdVerificationStatus: true,
    getDraft?: boolean,
  },
): {
  seller: SellerWithIdVerification,
  isLoading: boolean,
  error: any,
  mutateLoggedInSeller: KeyedMutator<SellerWithIdVerification>
};
export function useLoggedInSeller(
  propertyId: number,
  opt?: {
    refreshInterval?: number,
    shouldFetch?: boolean,
    getIdVerificationStatus?: boolean,
    getDraft?: boolean,
  },
) {
  const shouldFetch = opt?.shouldFetch !== false;
  const getIdVerificationStatus = opt?.getIdVerificationStatus === true;
  const getDraft = opt?.getDraft === true;

  const { data, error, mutate } = useSWR(
    shouldFetch && propertyId
      ? [
          LOGGED_IN_SELLER_KEY,
          propertyId,
          getIdVerificationStatus,
          getDraft,
        ]
      : null,
    getLoggedInSeller,
    { refreshInterval: opt?.refreshInterval }
  );

  return {
    seller: data,
    isLoading: Boolean(propertyId) && shouldFetch && !error && data === undefined,
    error: error,
    mutateLoggedInSeller: mutate,
  };
}

export const useContractDocument = (
  propertyId: number,
  locale: string,
  opt?: { shouldFetch?: boolean },
) => {
  const shouldFetch = opt?.shouldFetch !== false;

  const { data, error, mutate } = useSWR(
    shouldFetch
      ? [CONTRACT_DOC_KEY, propertyId, locale]
      : null,
    getContractDocument
  );

  return {
    contract: data,
    isLoading: shouldFetch && !error && !data,
    error: error,
    mutateContractDocument: mutate,
  };
}

export function useReviewGroupSections(
  propertyId: number,
  opt?: { shouldFetch?: boolean },
) {
  const shouldFetch = opt?.shouldFetch !== false;

  const { data, error, mutate } = useSWR(
    shouldFetch && propertyId
      ? [REVIEW_GROUP_SECTIONS_KEY, propertyId]
      : null,
    getReviewGroupSections
  );

  return {
    reviewGroupSections: data,
    isReviewGroupSectionsLoading: shouldFetch
      && propertyId
      && !data
      && !error,
    error,
    mutateReviewGroupSections: mutate,
  }
}

export const mutateReviewGroupSections = async (propertyId: number) => {
  await mutate([REVIEW_GROUP_SECTIONS_KEY, propertyId]);
}
